import type { MessageDescriptor } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import type { MathPracticeSheet } from '../content/math-library';
import type { MathProblem, SmAnswer, SmProblem } from '../content/schemas';
import { MATH_SESSION_LENGTH, mathQuestion } from './exercise';

/**
 * Library › Math and the Quick math exercise: the Math library's categories and levels, and
 * every problem (a workbook sum or a Math library problem) turned into the one shape the
 * exercise asks — a figure, four choices and the answer. Shared by web and mobile; the
 * screens draw the figures.
 */
export type MathCategory =
  | 'addition'
  | 'subtraction'
  | 'missing-numbers'
  | 'number-patterns'
  | 'visual';

/** In the design's order. */
export const MATH_CATEGORIES: readonly MathCategory[] = [
  'addition',
  'subtraction',
  'missing-numbers',
  'number-patterns',
  'visual',
];

export const MATH_CATEGORY_LABEL: Record<MathCategory, MessageDescriptor> = {
  addition: msg`Addition`,
  subtraction: msg`Subtraction`,
  'missing-numbers': msg`Missing numbers`,
  'number-patterns': msg`Number patterns`,
  visual: msg`Visual math`,
};

export function isMathCategory(key: string): key is MathCategory {
  return (MATH_CATEGORIES as readonly string[]).includes(key);
}

/** Library › Math's rows (PRD-2431), each the practice sheets of one kind of problem. */
export type MathSubcategory =
  | 'addition-1-digit'
  | 'addition-2-digit'
  | 'addition-3-digit'
  | 'addition-several'
  | 'subtraction-1-digit'
  | 'subtraction-2-digit'
  | 'subtraction-several'
  | 'missing-0-to-9'
  | 'missing-up-to-99'
  | 'count-backwards'
  | 'complete-the-pattern'
  | 'dice'
  | 'symbols'
  | 'clocks-add'
  | 'clocks-subtract';

/** In the design's order (Figma 4363:37025). */
export const MATH_SUBCATEGORIES: readonly {
  key: MathSubcategory;
  category: MathCategory;
  title: MessageDescriptor;
}[] = [
  { key: 'addition-1-digit', category: 'addition', title: msg`1-digit numbers` },
  { key: 'addition-2-digit', category: 'addition', title: msg`2-digit numbers` },
  { key: 'addition-3-digit', category: 'addition', title: msg`3-digit numbers` },
  { key: 'addition-several', category: 'addition', title: msg`Add three or more numbers` },
  { key: 'subtraction-1-digit', category: 'subtraction', title: msg`1-digit numbers` },
  { key: 'subtraction-2-digit', category: 'subtraction', title: msg`2-digit numbers` },
  {
    key: 'subtraction-several',
    category: 'subtraction',
    title: msg`Subtract three or more numbers`,
  },
  {
    key: 'missing-0-to-9',
    category: 'missing-numbers',
    title: msg`Find the missing number (0 to 9)`,
  },
  {
    key: 'missing-up-to-99',
    category: 'missing-numbers',
    title: msg`Find the missing number (up to 99)`,
  },
  { key: 'count-backwards', category: 'number-patterns', title: msg`Count backwards` },
  { key: 'complete-the-pattern', category: 'number-patterns', title: msg`Complete the pattern` },
  { key: 'dice', category: 'visual', title: msg`Add the dice` },
  { key: 'symbols', category: 'visual', title: msg`Solve symbol puzzles` },
  { key: 'clocks-add', category: 'visual', title: msg`Clocks: add minutes` },
  { key: 'clocks-subtract', category: 'visual', title: msg`Clocks: subtract minutes` },
];

/**
 * The row a practice sheet belongs in, by its first problem's kind. Sums go by the sheet's
 * largest number; a sum larger than the rows offer (4-digit addition, 3-digit subtraction) has
 * no row and is not shown.
 */
export function mathSubcategoryOf(problems: readonly MathProblem[]): MathSubcategory | undefined {
  const [first] = problems;
  switch (first?.kind) {
    case 'column': {
      const columns = problems.flatMap((problem) => (problem.kind === 'column' ? [problem] : []));
      const several = columns.some((problem) => problem.operands.length > 2);
      const digits = String(Math.max(...columns.flatMap((problem) => problem.operands))).length;
      if (first.op === '-') {
        if (several) return 'subtraction-several';
        if (digits > 2) return undefined;
        return digits === 1 ? 'subtraction-1-digit' : 'subtraction-2-digit';
      }
      if (several) return 'addition-several';
      if (digits > 3) return undefined;
      return digits === 1
        ? 'addition-1-digit'
        : digits === 2
          ? 'addition-2-digit'
          : 'addition-3-digit';
    }
    case 'missing': {
      const largest = Math.max(
        ...problems.map((problem) => (problem.kind === 'missing' ? problem.answer : 0)),
      );
      return largest <= 9 ? 'missing-0-to-9' : 'missing-up-to-99';
    }
    case 'countdown':
      return 'count-backwards';
    case 'sequence':
      return 'complete-the-pattern';
    case 'dice':
      return 'dice';
    case 'symbols':
      return 'symbols';
    case 'clock':
      return first.minutes < 0 ? 'clocks-subtract' : 'clocks-add';
    default:
      return undefined;
  }
}

/** The Math library's levels; a level the app doesn't know shows no tag. */
export const MATH_LEVEL_LABEL: Record<string, MessageDescriptor> = {
  easy: msg`Easy`,
  medium: msg`Medium`,
  challenge: msg`Challenge`,
};

export type MathLevel = 'easy' | 'medium' | 'challenge';

/** Quick math › "Pick for me": the levels to choose from, as the design words them. */
export const MATH_LEVEL_CHOICES: readonly { level: MathLevel; label: MessageDescriptor }[] = [
  { level: 'easy', label: msg`Easy` },
  { level: 'medium', label: msg`Medium` },
  { level: 'challenge', label: msg`Challenging` },
];

/**
 * "Pick for me" at `level`: the first row of that level, in the library's order, with a sheet
 * the person hasn't done; once every one is done, the first of that level again.
 */
export function pickMathSet<T extends { levels: string[]; sheets: { lineageUuid: string }[] }>(
  sets: readonly T[],
  level: MathLevel,
  used: ReadonlySet<string>,
): T | undefined {
  const atLevel = sets.filter((set) => set.levels.includes(level));
  return (
    atLevel.find((set) => set.sheets.some((sheet) => !used.has(sheet.lineageUuid))) ?? atLevel[0]
  );
}

/**
 * "Do more of…": the row of the Math library the person practised last, from their newest
 * Quick math attempt that used one of its sheets.
 */
export function lastMathSet<T extends { sheets: { lineageUuid: string }[] }>(
  sets: readonly T[],
  attempts: readonly {
    kind: string;
    completedAt?: string;
    startedAt: string;
    includes: string[];
  }[],
): T | undefined {
  const newest = [...attempts]
    .filter((attempt) => attempt.kind === 'math')
    .sort((x, y) => (y.completedAt ?? y.startedAt).localeCompare(x.completedAt ?? x.startedAt));
  for (const attempt of newest) {
    const set = sets.find((candidate) =>
      candidate.sheets.some((sheet) => attempt.includes.includes(sheet.lineageUuid)),
    );
    if (set) return set;
  }
  return undefined;
}

/**
 * Where a die's pips sit on its 82 × 82 face (Figma exercise/content/math › dice), by face:
 * three columns and three rows, the six as two rows of three.
 */
export const DICE_PIPS: Record<number, readonly (readonly [number, number])[]> = (() => {
  const [left, centre, right] = [17.57, 41, 64.43];
  const [top, middle, bottom] = [21.96, 41, 60.04];
  return {
    1: [[centre, middle]],
    2: [
      [left, top],
      [right, bottom],
    ],
    3: [
      [left, top],
      [centre, middle],
      [right, bottom],
    ],
    4: [
      [left, top],
      [right, top],
      [left, bottom],
      [right, bottom],
    ],
    5: [
      [left, top],
      [right, top],
      [centre, middle],
      [left, bottom],
      [right, bottom],
    ],
    6: [
      [left, top],
      [centre, top],
      [right, top],
      [left, bottom],
      [centre, bottom],
      [right, bottom],
    ],
  };
})();

/**
 * Where a clock's hands point for `time` ("h:mm"), in degrees clockwise from 12: the minute
 * hand by the minutes, the hour hand by the hour and how far into it.
 */
export function clockHands(time: string): { hour: number; minute: number } {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return { hour: ((hours % 12) + minutes / 60) * 30, minute: minutes * 6 };
}

/** The point `radius` from the centre of a `size` clock face, `degrees` clockwise from 12. */
export function clockPoint(size: number, degrees: number, radius: number): [number, number] {
  const radians = (degrees * Math.PI) / 180;
  return [size / 2 + radius * Math.sin(radians), size / 2 - radius * Math.cos(radians)];
}

// The symbols as the design draws them: the card suits, the smiley and the sun as emoji.
const SYMBOL_GLYPH: Record<string, string> = { '♦': '♦️', '♣': '♣️', '☺': '🙂', '☼': '☀️' };

/** One piece of an equation as the card draws it. */
export interface EquationPart {
  text: string;
  /** `sign`: + − = in the accent; `symbol`: what stands for a number; else as it is. */
  role: 'number' | 'sign' | 'symbol' | 'unknown';
}

/** "♦ + 1 = 2" as the card draws it: numbers, signs, symbols and the "?" to find. */
export function equationParts(equation: string): EquationPart[] {
  return equation
    .split(/\s+/)
    .filter(Boolean)
    .map((text): EquationPart => {
      if (/^\d+$/.test(text)) return { text, role: 'number' };
      if (/^[+\-−=]$/.test(text)) return { text: text === '-' ? '−' : text, role: 'sign' };
      if (text === '?') return { text, role: 'unknown' };
      return { text: SYMBOL_GLYPH[text] ?? text, role: 'symbol' };
    });
}

/** What the exercise shows for one question. */
export type MathFigure =
  /**
   * `operands` joined by `op`: across (24 + 37 = ?) or, `stacked`, in a column as on paper
   * (Figma exercise/content/math). Three or more operands are always a column.
   */
  | { kind: 'sum'; op: '+' | '-'; operands: number[]; stacked?: boolean }
  /** Dice faces to add up. */
  | { kind: 'dice'; dice: number[] }
  /** `a op __ = total`. */
  | { kind: 'missing'; a: number; op: '+' | '-'; total: number }
  /**
   * A symbol puzzle (Figma exercise/content/math › symbols): the clues that give each
   * symbol's value ("♦ + 1 = 2", "2 − ♣ = 1"), then the question ("♦ + ♣ = ?").
   */
  | { kind: 'symbols'; clues: string[]; equation: string }
  /**
   * The terms so far; the answer is the next. A countdown says what it takes away each time
   * ("Subtract 7 each time: 100, 93, ?").
   */
  | { kind: 'sequence'; terms: number[]; subtract?: number }
  /** A clock face showing `start`, and minutes to move it by (back, when negative). */
  | { kind: 'clock'; start: string; minutes: number };

export interface MathItem {
  figure: MathFigure;
  answer: number | string;
  /** Four choices, the answer among them, in an order fixed by the question. */
  options: (number | string)[];
  /** What the question came from, recorded with the answer. */
  from: { sum: SmProblem } | { problem: MathProblem; step?: number };
}

/** A workbook sheet's sums (or the generated ones), one question each. */
export function sumItems(problems: readonly SmProblem[]): MathItem[] {
  return problems.map((problem) => ({
    figure: { kind: 'sum', op: problem.op, operands: [problem.a, problem.b] },
    answer: problem.answer,
    options: numberChoices(problem.answer, problem.a * 1_000 + problem.b * 7 + opSeed(problem.op)),
    from: { sum: problem },
  }));
}

/**
 * A short generated session, for when there's no workbook: stable for a given `seed` (the
 * day), so it doesn't change while the person is in it.
 */
export function generatedItems(seed: number): MathItem[] {
  return Array.from({ length: MATH_SESSION_LENGTH }, (_, index) => {
    const { a, op, b, answer, options } = mathQuestion(seed * MATH_SESSION_LENGTH + index);
    return {
      figure: { kind: 'sum', op, operands: [a, b] },
      answer,
      options,
      from: { sum: { a, op, b, answer } },
    };
  });
}

/**
 * A Math library sheet's problems, one question each; a countdown asks one per step, the
 * numbers so far growing as the packet's blanks fill in.
 */
export function libraryItems(sheet: MathPracticeSheet): MathItem[] {
  const stacked = sheet.format === 'stacked';
  return sheet.problems.flatMap((problem) => problemItems(problem, stacked));
}

function problemItems(problem: MathProblem, stacked: boolean): MathItem[] {
  const numeric = (figure: MathFigure, answer: number, seed: number): MathItem => ({
    figure,
    answer,
    options: numberChoices(answer, seed),
    from: { problem },
  });
  switch (problem.kind) {
    case 'column':
      return [
        numeric(
          {
            kind: 'sum',
            op: problem.op,
            operands: problem.operands,
            ...(stacked ? { stacked } : {}),
          },
          problem.answer,
          seedOf(problem.operands) + opSeed(problem.op),
        ),
      ];
    case 'dice':
      return [numeric({ kind: 'dice', dice: problem.dice }, problem.answer, seedOf(problem.dice))];
    case 'missing':
      return [
        numeric(
          { kind: 'missing', a: problem.a, op: problem.op, total: problem.total },
          problem.answer,
          seedOf([problem.a, problem.total]) + opSeed(problem.op),
        ),
      ];
    case 'symbols': {
      // The whole puzzle on one card, as on the packet's page: its clues, then the question.
      const equation = `${problem.question} = ?`;
      return [
        {
          figure: { kind: 'symbols', equation, clues: problem.clues },
          answer: problem.answer,
          options: numberChoices(
            problem.answer,
            seedOf([...problem.clues.join(''), ...equation].map((c) => c.codePointAt(0) ?? 0)),
          ),
          from: { problem },
        },
      ];
    }
    case 'sequence':
      return [
        numeric({ kind: 'sequence', terms: problem.terms }, problem.answer, seedOf(problem.terms)),
      ];
    case 'countdown':
      return problem.answers.map((answer, step) => {
        const terms = [problem.start, ...problem.answers.slice(0, step)];
        return {
          figure: { kind: 'sequence', terms, subtract: problem.step },
          answer,
          options: numberChoices(answer, seedOf([terms.at(-1) ?? problem.start, problem.step])),
          from: { problem, step },
        };
      });
    case 'clock':
      return [
        {
          figure: { kind: 'clock', start: problem.start, minutes: problem.minutes },
          answer: problem.answer,
          options: clockChoices(problem.answer, seedOf([...problem.start, problem.minutes])),
          from: { problem },
        },
      ];
  }
}

/** The answer to record for question `index` of an exercise. */
export function mathAnswer(
  item: MathItem,
  index: number,
  chosen: number | string,
  responseMs: number,
): SmAnswer {
  const common = { index, isCorrect: chosen === item.answer, responseMs };
  if ('sum' in item.from) {
    const { a, op, b } = item.from.sum;
    return {
      ...common,
      a,
      op,
      b,
      options: item.options as number[],
      chosen: chosen as number,
      correct: item.answer as number,
    };
  }
  return {
    ...common,
    problem: item.from.problem,
    ...(item.from.step !== undefined ? { step: item.from.step } : {}),
    options: item.options,
    chosen,
    correct: item.answer,
  };
}

/**
 * What to do, over the question, where it isn't the exercise's own "Select an answer" (sums,
 * dice and symbols, as the design has them).
 */
export function mathItemPrompt(figure: MathFigure): MessageDescriptor | undefined {
  switch (figure.kind) {
    case 'missing':
      return msg`Find the missing number`;
    case 'sequence':
      return msg`What comes next?`;
    case 'clock': {
      const minutes = Math.abs(figure.minutes);
      return figure.minutes >= 0
        ? msg`What time will it be in ${plural(minutes, { one: '# minute', other: '# minutes' })}?`
        : msg`What time was it ${plural(minutes, { one: '# minute', other: '# minutes' })} ago?`;
    }
    case 'sum':
    case 'symbols':
      return undefined;
  }
}

/** The question as a sentence, for screen readers (the figure is drawn for the eye). */
export function mathItemLabel(figure: MathFigure): MessageDescriptor {
  switch (figure.kind) {
    case 'sum': {
      const [a = 0, b = 0] = figure.operands;
      if (figure.operands.length === 2) {
        return figure.op === '+'
          ? msg`${a} plus ${b} equals what?`
          : msg`${a} minus ${b} equals what?`;
      }
      const expression = figure.operands.join(figure.op === '+' ? ' + ' : ' − ');
      return msg`What is ${expression}?`;
    }
    case 'dice': {
      const faces = figure.dice.join(', ');
      return msg`Dice showing ${faces}. What do they add up to?`;
    }
    case 'missing': {
      const { a, total } = figure;
      return figure.op === '+'
        ? msg`${a} plus what equals ${total}?`
        : msg`${a} minus what equals ${total}?`;
    }
    case 'symbols': {
      const { equation } = figure;
      const clues = figure.clues.join('; ');
      return msg`${clues}. ${equation}. What number is missing?`;
    }
    case 'sequence': {
      const terms = figure.terms.join(', ');
      const { subtract } = figure;
      return subtract === undefined
        ? msg`${terms}. What comes next?`
        : msg`Subtract ${subtract} each time: ${terms}. What comes next?`;
    }
    case 'clock': {
      const { start } = figure;
      const minutes = Math.abs(figure.minutes);
      return figure.minutes >= 0
        ? msg`The clock shows ${start}. What time will it be ${plural(minutes, {
            one: '# minute',
            other: '# minutes',
          })} later?`
        : msg`The clock shows ${start}. What time was it ${plural(minutes, {
            one: '# minute',
            other: '# minutes',
          })} earlier?`;
    }
  }
}

/**
 * The answer and three nearby distractors, never below zero, in an order fixed by `seed` so a
 * sheet looks the same every time. Choices are a touch-friendly way to move on, not a test.
 */
export function numberChoices(answer: number, seed: number): number[] {
  const random = mulberry32(seed);
  const magnitude = answer >= 100 ? 10 : 1;
  const choices = new Set([answer]);
  while (choices.size < 4) {
    const offset = (1 + Math.floor(random() * 9)) * magnitude * (random() < 0.5 ? -1 : 1);
    const candidate = answer + offset;
    if (candidate >= 0) choices.add(candidate);
  }
  return shuffled([...choices], random);
}

/** The answer and three times near it ("h:mm", 12-hour, as the library writes them). */
export function clockChoices(answer: string, seed: number): string[] {
  const random = mulberry32(seed);
  const offsets = [-60, -30, -15, -10, -5, 5, 10, 15, 30, 60];
  const choices = new Set([answer]);
  while (choices.size < 4) {
    const offset = offsets[Math.floor(random() * offsets.length)] as number;
    choices.add(addMinutes(answer, offset));
  }
  return shuffled([...choices], random);
}

/** `time` ("h:mm" on a 12-hour clock) moved by `minutes`. */
export function addMinutes(time: string, minutes: number): string {
  const [hours = 0, mins = 0] = time.split(':').map(Number);
  const total = ((((hours % 12) * 60 + mins + minutes) % 720) + 720) % 720;
  const hour = Math.floor(total / 60);
  return `${hour === 0 ? 12 : hour}:${String(total % 60).padStart(2, '0')}`;
}

const opSeed = (op: '+' | '-') => (op === '+' ? 1 : 2);

function seedOf(values: readonly (number | string)[]): number {
  let seed = 17;
  for (const value of values) {
    const n = typeof value === 'number' ? value : (value.codePointAt(0) ?? 0);
    seed = (Math.imul(seed, 31) + n) >>> 0;
  }
  return seed;
}

function shuffled<T>(items: T[], random: () => number): T[] {
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const swap = items[i] as T;
    items[i] = items[j] as T;
    items[j] = swap;
  }
  return items;
}

/** Deterministic PRNG (mulberry32). */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
